import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, MessageSquare, FolderKanban, FileText, Settings, LogOut, Menu, X, ExternalLink, Layers, BarChart3, Sliders, Package, ClipboardList, Newspaper } from 'lucide-react';

const DashboardLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
    { to: '/admin/content', icon: FileText, label: 'Content' },
    { to: '/admin/sections', icon: Layers, label: 'Sections' },
    { to: '/admin/products-manage', icon: Package, label: 'Products' },
    { to: '/admin/articles', icon: Newspaper, label: 'Articles' },
    { to: '/admin/quote-requests', icon: ClipboardList, label: 'Quote Requests' },
    { to: '/admin/analyses', icon: BarChart3, label: 'Analyses' },
    { to: '/admin/tool-settings', icon: Sliders, label: 'Analyzer' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
      isActive
        ? 'bg-blue-50 text-blue-700 shadow-sm'
        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
    }`;

  return (
    <div data-testid="dashboard-layout" className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 start-0 z-40 w-64 bg-white border-e border-slate-100 flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="p-6 flex items-center gap-3 border-b border-slate-50">
          <img
            src="https://customer-assets.emergentagent.com/job_9ff69888-dc97-4485-a373-d9d7220b18c1/artifacts/u79yfuct_Nexa_logo%20icon%20blue.svg"
            alt="Nexa"
            className="h-8 w-8"
          />
          <span className="font-bold text-lg text-slate-800 tracking-wider">NEXA</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={linkClass}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-50 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-blue-600 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View Website
          </a>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:text-red-500 transition-colors w-full"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/20" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ms-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <button
            className="lg:hidden p-2 -ms-2 text-slate-500"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="lg:block hidden" />
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">{user?.name || user?.username}</p>
              <p className="text-xs text-slate-400">{user?.role}</p>
            </div>
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
              {(user?.username || 'A')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
